import api, { ApiError } from './api';

/**
 * Expense Service - Manages expense operations
 * Creates expenses in the expenses collection for proper breakdown reporting
 */

class ExpenseService {
  constructor() {
    this.basePath = '/expenses';
  }

  /**
   * Create a new expense
   * @param {Object} expenseData - Expense data
   * @returns {Promise<Object>} Created expense
   */
  async createExpense(expenseData) {
    try {
      // Prepare expense payload for the backend expense service
      const payload = {
        category: expenseData.category,
        subcategory: expenseData.subcategory || '',
        vendor: expenseData.vendor,
        amount: parseFloat(expenseData.amount) || 0,
        taxAmount: parseFloat(expenseData.taxAmount) || 0,
        totalAmount: expenseData.totalAmount || (parseFloat(expenseData.amount) + parseFloat(expenseData.taxAmount || 0)),
        expenseDate: expenseData.expenseDate,
        dueDate: expenseData.dueDate || null,
        description: expenseData.description,
        notes: expenseData.notes || '',
        paymentMethod: expenseData.paymentMethod || 'credit_card',
        paymentReference: expenseData.paymentReference || '',
        vehicleId: expenseData.vehicleId || null,
        driverId: expenseData.driverId || null,
        recurring: expenseData.isRecurring || false,
        recurringPeriod: expenseData.recurringFrequency || null,
        billable: expenseData.isBillable || false,
        billableAmount: expenseData.billableAmount || null,
        customerId: expenseData.customerId || null,
        status: 'approved', // Set as approved by default for immediate visibility
        attachments: expenseData.attachments || []
      };

      const response = await api.post(this.basePath, payload);
      // Note: api.post() already returns response.data, so response is the actual data object
      return response?.expense || response;
    } catch (error) {
      console.error('Failed to create expense transaction:', error);
      throw error instanceof ApiError ? error : ApiError.fromResponse(error);
    }
  }

  /**
   * Get list of expenses with optional filters
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} List of expenses
   */
  async listExpenses(params = {}) {
    try {
      const response = await api.get(this.basePath, { params });
      // Note: api.get() already returns response.data, so response is the actual data object
      return response || { expenses: [], total: 0 };
    } catch (error) {
      console.error('Failed to list expenses:', error);
      throw error instanceof ApiError ? error : ApiError.fromResponse(error);
    }
  }

  /**
   * Get a specific expense by ID
   * @param {string} expenseId - Expense ID
   * @returns {Promise<Object>} Expense data
   */
  async getExpense(expenseId) {
    try {
      if (!expenseId) {
        throw new ApiError('Expense ID is required');
      }

      const response = await api.get(`${this.basePath}/${expenseId}`);
      // Note: api.get() already returns response.data, so response is the actual data object
      return response || null;
    } catch (error) {
      console.error('Failed to get expense:', error);
      throw error instanceof ApiError ? error : ApiError.fromResponse(error);
    }
  }

  /**
   * Update an expense
   * @param {string} expenseId - Expense ID
   * @param {Object} updateData - Updated expense data
   * @returns {Promise<Object>} Updated expense
   */
  async updateExpense(expenseId, updateData) {
    try {
      if (!expenseId) {
        throw new ApiError('Expense ID is required');
      }

      // Recalculate total if amount or tax changes
      if (updateData.amount !== undefined || updateData.taxAmount !== undefined) {
        updateData.totalAmount = (parseFloat(updateData.amount) || 0) + (parseFloat(updateData.taxAmount) || 0);
      }

      const response = await api.put(`${this.basePath}/${expenseId}`, updateData);
      // Note: api.put() already returns response.data, so response is the actual data object
      return response;
    } catch (error) {
      console.error('Failed to update expense:', error);
      throw error instanceof ApiError ? error : ApiError.fromResponse(error);
    }
  }

  /**
   * Delete an expense
   * @param {string} expenseId - Expense ID
   * @returns {Promise<void>}
   */
  async deleteExpense(expenseId) {
    try {
      if (!expenseId) {
        throw new ApiError('Expense ID is required');
      }

      await api.delete(`${this.basePath}/${expenseId}`);
    } catch (error) {
      console.error('Failed to delete expense:', error);
      throw error instanceof ApiError ? error : ApiError.fromResponse(error);
    }
  }

  /**
   * Get expense breakdown by category
   * @param {Object} params - Query parameters (startDate, endDate, etc.)
   * @returns {Promise<Object>} Expense breakdown
   */
  async getExpenseBreakdown(params = {}) {
    try {
      const endpoint = params.dimension
        ? `${this.basePath}/breakdown/${params.dimension}`
        : `${this.basePath}/breakdown/category`;

      const response = await api.get(endpoint, { params });
      // Note: api.get() already returns response.data, so response is the actual data object
      return response || { breakdown: [], grandTotal: 0 };
    } catch (error) {
      console.error('Failed to get expense breakdown:', error);
      throw error instanceof ApiError ? error : ApiError.fromResponse(error);
    }
  }

  /**
   * Get expense trends over time
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} Expense trends
   */
  async getExpenseTrends(params = {}) {
    try {
      const response = await api.get(`${this.basePath}/trends`, { params });
      // Note: api.get() already returns response.data, so response is the actual data object
      return response || { trends: [] };
    } catch (error) {
      console.error('Failed to get expense trends:', error);
      throw error instanceof ApiError ? error : ApiError.fromResponse(error);
    }
  }

  /**
   * Approve an expense
   * @param {string} expenseId - Expense ID
   * @returns {Promise<Object>} Updated expense
   */
  async approveExpense(expenseId) {
    try {
      if (!expenseId) {
        throw new ApiError('Expense ID is required');
      }

      const response = await api.put(`${this.basePath}/${expenseId}/approve`);
      // Note: api.put() already returns response.data, so response is the actual data object
      return response;
    } catch (error) {
      console.error('Failed to approve expense:', error);
      throw error instanceof ApiError ? error : ApiError.fromResponse(error);
    }
  }

  /**
   * Reject an expense
   * @param {string} expenseId - Expense ID
   * @param {string} reason - Rejection reason
   * @returns {Promise<Object>} Updated expense
   */
  async rejectExpense(expenseId, reason) {
    try {
      if (!expenseId) {
        throw new ApiError('Expense ID is required');
      }

      const response = await api.put(`${this.basePath}/${expenseId}/reject`, { reason });
      // Note: api.put() already returns response.data, so response is the actual data object
      return response;
    } catch (error) {
      console.error('Failed to reject expense:', error);
      throw error instanceof ApiError ? error : ApiError.fromResponse(error);
    }
  }

  /**
   * Bulk create expenses
   * @param {Array} expenses - Array of expense objects
   * @returns {Promise<Object>} Created expenses
   */
  async bulkCreateExpenses(expenses) {
    try {
      if (!Array.isArray(expenses) || expenses.length === 0) {
        throw new ApiError('Expenses array is required');
      }

      const response = await api.post(`${this.basePath}/bulk`, { expenses });
      // Note: api.post() already returns response.data, so response is the actual data object
      return response || { created: [], failed: [] };
    } catch (error) {
      console.error('Failed to bulk create expenses:', error);
      throw error instanceof ApiError ? error : ApiError.fromResponse(error);
    }
  }

  /**
   * Get expense summary for dashboard
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} Expense summary
   */
  async getExpenseSummary(params = {}) {
    try {
      const response = await api.get(`${this.basePath}/summary`, { params });
      // Note: api.get() already returns response.data, so response is the actual data object
      return response || {
        totalExpenses: 0,
        averageDaily: 0,
        topCategories: [],
        pendingApproval: 0
      };
    } catch (error) {
      console.error('Failed to get expense summary:', error);
      throw error instanceof ApiError ? error : ApiError.fromResponse(error);
    }
  }
}

// Export singleton instance
const expenseService = new ExpenseService();
export default expenseService;